// Hợp đồng của audit-service (đặc tả docs/backend/services/audit-service.md): nhật ký bất biến từ MỌI sự kiện
// tích hợp (identity, hr, crm, inventory, booking, deal, attendance, performance, commission), chuỗi băm theo tenant,
// truy vấn màn Nhật ký, kiểm chứng toàn vẹn. audit không lưu PII (SĐT, email, CCCD, lương, nội dung chăm sóc).
import { z } from 'zod';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const msg = (message: string) => ({ errorMap: () => ({ message }) });
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const isRealDate = (s: string) => {
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};
const dayReq = (required = 'Chọn ngày') => str(required).regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày không hợp lệ').refine(isRealDate, 'Ngày không hợp lệ');
const day = dayReq();
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};
const boolish = z.preprocess(
  (v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : undefined),
  z.boolean().optional(),
);
const splitList = (v: unknown) => {
  if (v === undefined || v === null || v === '') return undefined;
  return (Array.isArray(v) ? v.flatMap((x) => String(x).split(',')) : String(v).split(',')).map((s) => s.trim()).filter(Boolean);
};

// ---------- Hằng số ----------
export const AUDIT_CATEGORIES = ['business', 'security', 'activity', 'system'] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];
/** Lớp lưu trữ = chuỗi băm: long 5 năm, standard 2 năm, short 180 ngày. */
export const AUDIT_CHAINS = ['long', 'standard', 'short'] as const;
export type AuditChain = (typeof AUDIT_CHAINS)[number];
export const VERIFICATION_STATUSES = ['running', 'passed', 'failed'] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];
/** Stream được ghi nhật ký (không evt:audit, không evt:notification — notification không phát). */
export const AUDITED_CONTEXTS = ['identity', 'hr', 'crm', 'inventory', 'booking', 'deal', 'attendance', 'performance', 'commission'] as const;
export const VERIFY_MAX_DAYS = 366;

// ---------- Lệnh ----------
/** Kiểm chứng chuỗi băm theo khoảng ngày (≤ 366 ngày); 202 { verificationId }. */
export const verifyAuditChainInput = z
  .object({ fromDay: dayReq('Chọn từ ngày'), toDay: dayReq('Chọn đến ngày'), chain: z.enum(AUDIT_CHAINS, msg('Chuỗi không hợp lệ')).optional() })
  .refine((v) => v.fromDay <= v.toDay, { message: 'Đến ngày phải sau hoặc bằng từ ngày', path: ['toDay'] });

// ---------- Truy vấn ----------
export const listAuditEntriesInput = z.object({
  /** Mặc định hôm nay (VN); ngày tương lai ⇒ 422 AUDIT_DAY_IN_FUTURE. */
  day: day.optional(),
  actorStaffId: uuid.optional(),
  /** Một hoặc nhiều mã thao tác: a,b. */
  action: z.preprocess(splitList, z.array(text(120)).max(50, 'Tối đa 50 thao tác').optional()),
  /** "Chỉ thao tác nhạy cảm". */
  sensitive: boolish,
  category: z.enum([...AUDIT_CATEGORIES, 'all'] as const, msg('Loại không hợp lệ')).default('business'),
  q: text(200).optional(),
  subjectId: text(100).optional(),
  cursor: text(200).optional(),
  limit: qInt(1, 500, 100),
});
export const getAuditEntryInput = z.object({ id: uuid });
export const listAuditActionsInput = z.object({});
export const getAuditSubjectHistoryInput = z.object({
  subjectType: str('Thiếu loại đối tượng').trim().min(1, 'Thiếu loại đối tượng').max(40, 'Tối đa 40 ký tự'),
  subjectId: str('Thiếu đối tượng').trim().min(1, 'Thiếu đối tượng').max(100, 'Tối đa 100 ký tự'),
  cursor: text(200).optional(),
  limit: qInt(1, 200, 50),
  /** Mặc định chỉ dòng nghiệp vụ; 'all' gồm cả dòng hệ thống (A-T24). */
  category: z.enum([...AUDIT_CATEGORIES, 'all'] as const, msg('Loại không hợp lệ')).default('business'),
});
export const getAuditIntegrityStatusInput = z.object({});
export const getAuditVerificationInput = z.object({ verificationId: uuid });

/** Tên RPC: audit.cmd.<Name> / audit.qry.<Name>. */
export const RPC = {
  cmd: ['VerifyAuditChain'],
  qry: ['ListAuditEntries', 'GetAuditEntry', 'ListAuditActions', 'GetAuditSubjectHistory', 'GetAuditIntegrityStatus', 'GetAuditVerification'],
} as const;

// ---------- DTO ----------
export interface AuditRow {
  id: string;
  occurredAt: string;
  actor: { staffId: string | null; /** "Hệ thống" khi null */ name: string; onBehalf: boolean };
  /** Mã ổn định, VD 'booking.unit.released:override'. */
  action: string;
  label: string;
  category: AuditCategory;
  sensitive: boolean;
  /**
   * label ⟨bổ sung⟩: tên hiển thị của đối tượng dựng lúc đọc (mã căn, tên khách, mã giao dịch, tên nhân sự, tên dự án, tên nhóm,
   * tên vai trò, tên người dùng / người xin tham gia, tên công ty); null / vắng = không suy ra được (FE tự suy hoặc hiện id rút gọn).
   */
  subject: { type: string; id: string; label?: string | null };
  refs: Record<string, string>;
  /** ⟨bổ sung⟩ Tên hiển thị của refs theo khoá (customerId ⇒ tên khách, unitId ⇒ mã căn, dealId ⇒ mã giao dịch, staffId…); chỉ khoá tra được. */
  refLabels?: Record<string, string>;
  /** Dựng lúc đọc theo thứ tự describe() của prototype. */
  summary: string;
  /** > 1 khi gộp hiển thị. */
  count: number;
}
export interface AuditEntryList {
  day: string;
  items: AuditRow[];
  nextCursor: string | null;
  people: { staffId: string; name: string }[];
  counts: { all: number; sensitive: number };
}
export interface AuditEntryDetail extends AuditRow {
  detail: Record<string, unknown>;
  /**
   * ⟨bổ sung⟩ id nhân sự / vai trò / nhóm nằm trong detail ⇒ tên hiển thị (owner, holder, staff, successorId, handoverToStaffId,
   * leaderId, managerId, invitedByStaffId; from / to của crm.customer.assigned, returnedToPool, crm.task.*, hr.offboarding.*; role /
   * roleId và from / to của identity.account.roleAssigned, identity.role.created; fromTeamId / toTeamId). Chỉ id tra được.
   */
  detailNames?: Record<string, string>;
  source: { service: string; type: string; eventId: string; schemaVersion: number; aggregateId: string; aggregateVersion: number | null; correlationId: string; causationId: string | null };
  chain: AuditChain;
  seq: number;
  /** hex SHA-256. */
  prevHash: string;
  hash: string;
  verified: boolean;
}
export interface AuditActionItem {
  action: string;
  label: string;
  category: AuditCategory;
  sensitive: boolean;
}
export interface AuditSubjectHistory {
  items: AuditRow[];
  nextCursor: string | null;
}
export interface AuditChainStatus {
  chain: AuditChain;
  lastSeq: number;
  lastAnchoredDay: string | null;
  lastVerification: { status: VerificationStatus; finishedAt: string | null; fromDay: string; toDay: string; firstBadSeq?: number | null } | null;
  lastRetentionCut: { uptoSeq: number; uptoDay: string; purgedAt: string } | null;
}
export interface AuditIntegrityStatus {
  chains: AuditChainStatus[];
}
export interface AuditVerificationView {
  verificationId: string;
  chain: AuditChain;
  fromDay: string;
  toDay: string;
  fromSeq: number | null;
  toSeq: number | null;
  status: VerificationStatus;
  firstBadSeq: number | null;
  startedAt: string;
  finishedAt: string | null;
}
export interface VerifyAuditChainResult {
  /** Lượt đầu tiên (chuỗi đầu tiên được kiểm). */
  verificationId: string;
  /** Mỗi chuỗi một lượt (không chọn chuỗi ⇒ long, standard, short — chạy tuần tự). */
  verificationIds: string[];
}

// ---------- Sự kiện ----------
export const EVENTS = {
  integrityViolationDetected: 'audit.integrity.violationDetected',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

/** aggregateId = verificationId. Một lần cho mỗi firstBadSeq (H4). */
export interface IntegrityViolationDetected {
  verificationId: string;
  chain: AuditChain;
  fromSeq: number;
  toSeq: number;
  firstBadSeq: number;
  /** hex SHA-256. */
  expectedHash: string;
  actualHash: string;
  detectedAt: string;
  firstBadDay: string;
}

export interface EventPayloads {
  [EVENTS.integrityViolationDetected]: IntegrityViolationDetected;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
